import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  IconButton,
  InputAdornment,
  TextField,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useThemeContext } from '../../../theme/ThemeContextProvider';
import { useGetPaginatedUsersQuery } from '../../../store/apiSlice/searchApi';
import { useNavigate } from 'react-router-dom';
import SearchIcon from '../../Icons/SearchIcon';
import MobileSearchModal from './MobileSearchModal';
import SearchBarList from './SearchBarList';

const RESPONSE_PAGE = 1;
const RESPONSE_ITEMS_LIMIT = 5;
const SORT_OPTION = 'asc';

interface SearchBarProps {
  setIsSearchModalOpenOnMobile: (arg: boolean) => void;
}

const SearchBar: FC<SearchBarProps> = ({
  setIsSearchModalOpenOnMobile,
}: SearchBarProps) => {
  const { theme } = useThemeContext();
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const navigate = useNavigate();

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [search, setSearch] = useState<string>('');
  const searchBarRef = useRef<HTMLDivElement | null>(null);
  const searchFieldRef = useRef<HTMLInputElement | null>(null);

  const { data: users, isLoading } = useGetPaginatedUsersQuery(
    {
      page: RESPONSE_PAGE,
      limit: RESPONSE_ITEMS_LIMIT,
      search,
      sort: SORT_OPTION,
    },
    { skip: search.length === 0 }
  );

  const openSearchBar = () => {
    setIsOpen(true);

    if (isMobile) {
      setIsSearchModalOpenOnMobile(true);
    }
  };

  const closeSearchBar = () => {
    setIsOpen(false);
    setSearch('');
    setIsSearchModalOpenOnMobile(false);
  };

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(event.target.value);
    },
    []
  );

  const handleSearchSubmit = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && search.trim().length > 0) {
      event.preventDefault();
      navigate(`/global-search?query=${encodeURIComponent(search.trim())}`);
      closeSearchBar();
    }
  };

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchBarRef.current &&
        !searchBarRef.current.contains(event.target as Node)
      ) {
        closeSearchBar();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]); // Intentionally omitting closeSearchBar - custom hook function not memoized

  useEffect(() => {
    if (isOpen && searchFieldRef.current) {
      searchFieldRef.current.focus();
    }
  }, [isOpen]);

  if (!isOpen) {
    return (
      <IconButton
        onClick={openSearchBar}
        sx={{
          ...iconStyles,
          '&:hover': {
            backgroundColor: theme.palette.text.accent,
            color: theme.palette.background.white,
          },
        }}
      >
        <SearchIcon sx={iconSizes} />
      </IconButton>
    );
  }

  const closeSearchBarButton = () => (
    <IconButton
      aria-label="Close search field"
      onClick={closeSearchBar}
      sx={iconStyles}
    >
      <CloseIcon sx={iconSizes} />
    </IconButton>
  );

  if (isMobile) {
    return (
      <>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '6px',
          }}
        >
          <TextField
            variant="outlined"
            placeholder="Search..."
            inputRef={searchFieldRef}
            value={search}
            onChange={handleSearchChange}
            onKeyDown={handleSearchSubmit}
            sx={searchFieldStyles}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ p: 0 }}>
                  <SearchIcon sx={iconSizes} />
                </InputAdornment>
              ),
            }}
          />

          {closeSearchBarButton()}
        </Box>

        {isOpen && (
          <MobileSearchModal
            search={search}
            isOpen={isOpen}
            onClose={closeSearchBar}
            isLoading={isLoading}
            users={users}
          />
        )}
      </>
    );
  }

  return (
    <Box ref={searchBarRef} sx={{ position: 'relative' }}>
      <TextField
        variant="outlined"
        placeholder="Search..."
        inputRef={searchFieldRef}
        value={search}
        onChange={handleSearchChange}
        onKeyDown={handleSearchSubmit}
        sx={searchFieldStyles}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start" sx={{ p: 0 }}>
              <SearchIcon sx={iconSizes} />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              {closeSearchBarButton()}
            </InputAdornment>
          ),
        }}
      />

      {search.trim().length > 0 && users && users.data.length > 0 && (
        <Box
          sx={{
            position: 'absolute',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            padding: '0.75rem 0',
            zIndex: 10,
            backgroundColor: theme.palette.background.white,
          }}
        >
          <SearchBarList
            search={search}
            isOpen={isOpen}
            onClose={closeSearchBar}
            isLoading={isLoading}
            users={users}
          />
        </Box>
      )}
    </Box>
  );
};

export default SearchBar;

const iconStyles = {
  padding: 0,
  height: '2rem',
  width: '2rem',
  minHeight: '2rem',
  minWidth: '2rem',
};

const searchFieldStyles = {
  width: '100%',
  maxWidth: {
    md: '16rem',
    lg: '20.125rem',
  },
  '& .MuiInputBase-root': {
    height: '2rem',
    padding: '0.75rem 1rem',
  },
  '& .MuiOutlinedInput-input': {
    height: '2rem !important',
    padding: 0,
  },
  '& .MuiSvgIcon-root': {
    backgroundColor: 'transparent',
  },
};

const iconSizes = {
  width: '1.25rem',
  height: '1.25rem',
};
